import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTodayAttendance } from "@/api/hooks/attendance";
import { useMe } from "@/api/hooks/auth";
import { LogOut } from "@/shared/icons";
import { formatTime } from "@/shared/utils/format";
import { GeofenceLiveMap, type FencePoint } from "./GeofenceLiveMap";

/**
 * Logout-time attendance prompt — UI ported from the wm-modal* design.
 *
 * **Mark-out happens on the phone, not here.** The server refuses a mark-out
 * that is not signed by the employee's registered Workman mobile app, so this
 * prompt only reminds the user their day is still open. Two exits:
 *   1. "Sign out only" — signs out; attendance stays open to be marked out
 *      later from the registered phone.
 *   2. Cancel.
 */
export function LogoutAttendancePrompt({
  open,
  onCancel,
  onSignOutOnly,
}: {
  open: boolean;
  onCancel: () => void;
  onSignOutOnly: () => void;
}) {
  const att = useTodayAttendance(open);
  const attRow = att.data?.attendance ?? null;
  const me = useMe(open);
  const myLocations = me.data?.locations ?? [];
  const fences: FencePoint[] = myLocations.map((loc) => ({
    lat: loc.lat,
    lng: loc.lng,
    radiusM: loc.radiusM,
    name: loc.name,
    isPrimary: loc.isPrimary,
  }));
  const markedInCoords =
    attRow?.markedInLat && attRow?.markedInLng
      ? { lat: Number(attRow.markedInLat), lng: Number(attRow.markedInLng), label: "Mark-in" }
      : null;

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [open]);

  const elapsed = useMemo(() => {
    const at = attRow?.markedInAt ? new Date(attRow.markedInAt) : null;
    if (!at) return null;
    const ms = now.getTime() - at.getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return `${h}h ${m.toString().padStart(2, "0")}m`;
  }, [attRow?.markedInAt, now]);

  const markInTime = useMemo(
    () => (attRow?.markedInAt ? formatTime(attRow.markedInAt) : null),
    [attRow?.markedInAt],
  );

  if (!open) return null;

  const liveClockText = (() => {
    const d = now;
    return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}:${d.getSeconds().toString().padStart(2, "0")}`;
  })();

  return createPortal(
    <div className="wm-modal-overlay" onClick={onCancel}>
      <div
        className="wm-modal"
        style={{ width: 600 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="wm-modal-header">
          <div className="wm-modal-icon" style={{ color: "var(--wm-danger)" }}>
            <svg
              viewBox="0 0 16 16"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M10 11l3-3-3-3M13 8H6M6 3H3v10h3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="wm-modal-title">Sign out</div>
            <div className="wm-modal-sub">
              You have an open attendance row. Mark out on your phone before leaving.
            </div>
          </div>
          <button
            type="button"
            className="wm-modal-x"
            onClick={onCancel}
            aria-label="Cancel"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="wm-modal-body">
          {markInTime && (
            <div className="wm-mo-summary">
              <div className="wm-mo-stat">
                <div className="l">Marked in</div>
                <div className="v mono">{markInTime}</div>
              </div>
              <div className="wm-mo-stat">
                <div className="l">Live clock</div>
                <div className="v mono">{liveClockText}</div>
              </div>
              <div className="wm-mo-stat">
                <div className="l">Elapsed</div>
                <div
                  className="v mono"
                  style={{ color: "var(--wm-ok)" }}
                >
                  {elapsed ?? "—"}
                </div>
              </div>
            </div>
          )}

          <div className="wm-att-banner" role="note">
            <strong>Mark out from the Workman mobile app.</strong>
            <span>Attendance can only be marked on your registered phone.</span>
          </div>

          {/* Geofence preview + summary */}
          {attRow && (
            <div className="wm-mi-loc-stack">
              <GeofenceLiveMap
                fences={fences}
                user={null}
                marked={markedInCoords}
                within={attRow.markedInWithinGeofence ?? null}
                height={220}
              />
              <div className="wm-mi-loc-body">
                <div className="wm-mi-loc-row">
                  <span className="l">Mark-in location</span>
                  <span
                    className="v"
                    style={{
                      color:
                        attRow.markedInWithinGeofence === false
                          ? "var(--wm-danger)"
                          : "var(--wm-ok)",
                      fontWeight: 600,
                    }}
                  >
                    {attRow.markedInWithinGeofence === false ? "✕ Outside" : "✓ Within"}
                  </span>
                </div>
                <div className="wm-mi-loc-row">
                  <span className="l">Fence</span>
                  <span className="v">
                    {attRow.markedInGeofenceName ?? "—"}
                  </span>
                </div>
                <div className="wm-mi-loc-row">
                  <span className="l">Distance</span>
                  <span
                    className="v mono"
                    style={{
                      color:
                        attRow.markedInWithinGeofence === false
                          ? "var(--wm-danger)"
                          : "var(--wm-ok)",
                    }}
                  >
                    {attRow.markedInDistanceM != null
                      ? `${attRow.markedInDistanceM}m`
                      : "—"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="wm-modal-footer"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 11, color: "var(--wm-ink-3)" }}>
            "Sign out only" keeps attendance open — mark out later from your registered phone.
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="wm-btn sm" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="wm-btn sm"
              onClick={onSignOutOnly}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <LogOut size={12} /> Sign out only
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
